import React, { useMemo } from "react";
import { getCategoryText, getThresholdUi } from "../utils/taxRules";
import { resolveDisplayTaxYearLabel } from "../utils/taxYear.js";

function n(v) {
  const x = Number(v || 0);
  return Number.isFinite(x) ? x : 0;
}

function normalizeCountryCode(country = "DE") {
  return String(country || "DE").trim().toUpperCase().replace(/\s+/g, "_");
}

function isUkCountry(country = "") {
  const cc = normalizeCountryCode(country);
  return cc === "UK" || cc === "GB" || cc === "UNITED_KINGDOM";
}

function expectedCurrencyForCountry(country = "DE") {
  const cc = normalizeCountryCode(country);

  if (cc === "US" || cc === "USA" || cc === "UNITED_STATES") return "USD";
  if (isUkCountry(cc)) return "GBP";
  if (cc === "CH" || cc === "CHE" || cc === "SWITZERLAND") return "CHF";

  return "EUR";
}

function money(value, currency = "EUR", language = "de") {
  const locale =
    currency === "GBP"
      ? "en-GB"
      : language === "de"
        ? "de-DE"
        : "en-US";

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n(value));
}

function uiLanguage(language = "de") {
  return String(language || "").toLowerCase().startsWith("en") ? "en" : "de";
}

function getReportProfile(country = "DE", language = "de", year) {
  const cc = normalizeCountryCode(country);
  const lang = uiLanguage(language);
  const isDeLang = lang === "de";

  const disposal = getCategoryText({
    countryCode: cc,
    categoryKey: "disposal",
    language: lang,
    showNative: false,
  });
  const other = getCategoryText({
    countryCode: cc,
    categoryKey: "other",
    language: lang,
    showNative: false,
  });

  const thresholdUi = getThresholdUi({
    countryCode: cc,
    year,
    language: lang,
  });

  const currency = expectedCurrencyForCountry(cc);

  let disposalAllowance = isDeLang ? "Freigrenze: n/a" : "Exemption threshold: n/a";
  let incomeAllowance = disposalAllowance;

  if (thresholdUi.mode === "separate") {
    disposalAllowance = thresholdUi.disposalLabel || disposalAllowance;
    incomeAllowance = thresholdUi.otherLabel || incomeAllowance;
  } else if (thresholdUi.mode === "combined" && thresholdUi.combinedLabel) {
    disposalAllowance = thresholdUi.combinedLabel;
    if (isUkCountry(cc)) {
      const miscLabel =
        thresholdUi.miscAllowanceLabel?.[lang] ||
        thresholdUi.miscAllowanceLabel?.en ||
        (lang === "de" ? "Misc. allowance: £1.000" : "Misc. allowance: £1,000");
      incomeAllowance = miscLabel;
    } else {
      incomeAllowance = thresholdUi.combinedLabel;
    }
  } else if (cc === "US" || cc === "USA" || cc === "UNITED_STATES") {
    disposalAllowance = isDeLang ? "n. z." : "n/a";
    incomeAllowance = disposalAllowance;
  }

  return {
    currency,
    disposalTitle: disposal.primary,
    incomeTitle: other.primary,
    disposalAllowance,
    incomeAllowance,
    sectionTitle: isDeLang ? "Steuerzusammenfassung" : "Tax Summary",
    sectionLead: isDeLang
      ? "Übersicht der berechneten steuerrelevanten Werte"
      : "Overview of the calculated taxable values",
  };
}

function getUkIncomeAllowanceMeta(miscIncome, lang = "en") {
  const isDe = lang === "de";
  const base = isDe ? "Misc. allowance: £1.000" : "Misc. allowance: £1,000";
  const amount = Number(miscIncome || 0);

  if (amount > 2500) {
    return `${base} · ${
      isDe ? "Über £2.500: Self Assessment wahrscheinlich" : "Over £2,500: Self Assessment likely"
    }`;
  }
  if (amount > 1000) {
    return `${base} · ${
      isDe ? "£1.000–£2.500: HMRC kontaktieren" : "£1,000–£2,500: contact HMRC"
    }`;
  }
  return `${base} · ${isDe ? "Unter £1.000 Freibetrag" : "Below £1,000 allowance"}`;
}

export default function PrintReportDashboard({
  language = "de",
  selectedCountry = "DE",
  selectedYear,
  displayTaxYear,
  vetTotalProfit,
  vthoTotalProfit,
  b3trTotalProfit,
  nftTotalProfit,
  exchangeTotalProfit,
  liquidityTotalProfit,
  reportMode = "full",
}) {
  const profile = useMemo(
    () => getReportProfile(selectedCountry, language, selectedYear),
    [selectedCountry, language, selectedYear]
  );

  const currency = profile.currency;
  const country = String(selectedCountry || "DE").toUpperCase();
  const lang = uiLanguage(language);
  const isUK = isUkCountry(selectedCountry);
  const mode = String(reportMode || "full").toLowerCase();
  const showDisposal = mode === "full" || mode === "disposal";
  const showIncome = mode === "full" || mode === "income";

  const rows = useMemo(() => {
    const vet = vetTotalProfit || {};
    const vtho = vthoTotalProfit || {};
    const b3tr = b3trTotalProfit || {};
    const exchange = exchangeTotalProfit || {};
    const liquidity = liquidityTotalProfit || {};

    return [
      {
        label: "VET",
        disposal: n(vet.swapProfit),
        income: n(vet.otherIncome),
      },
      {
        label: "VTHO",
        disposal: n(vtho.swapProfit),
        income: n(vtho.otherIncome),
      },
      {
        label: "VIP-180",
        disposal: n(b3tr.swapProfit),
        income: n(b3tr.otherIncome),
      },
      {
        label: "NFT",
        disposal: n(nftTotalProfit),
        income: 0,
      },
      {
        label: "Liquidity",
        disposal: n(liquidity.swapProfit),
        income: 0,
      },
      {
        label: "Exchange",
        disposal: n(exchange.swapProfit),
        income: n(exchange.otherIncome),
      },
    ];
  }, [
    vetTotalProfit,
    vthoTotalProfit,
    b3trTotalProfit,
    nftTotalProfit,
    exchangeTotalProfit,
    liquidityTotalProfit,
  ]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.disposal += n(row.disposal);
        acc.income += n(row.income);
        return acc;
      },
      { disposal: 0, income: 0 }
    );
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (showIncome && !showDisposal) return rows.filter((row) => n(row.income) !== 0);
    if (showDisposal && !showIncome) return rows.filter((row) => n(row.disposal) !== 0);
    return rows;
  }, [rows, showIncome, showDisposal]);

  const categoryLabel = lang === "de" ? "Kategorie" : "Category";

  const rowLabel = (k) => {
    if (k === "Liquidity") return "Liquidity";
    if (k === "Exchange") {
      return lang === "de" && country === "DE" ? "Börsen-Importe" : "Exchange";
    }
    return k;
  };

  const incomeAllowanceMeta = isUK
    ? getUkIncomeAllowanceMeta(totals.income, lang)
    : profile.incomeAllowance;

  const yearLabel = resolveDisplayTaxYearLabel(selectedYear, selectedCountry, displayTaxYear);

  return (
    <section className="tr-dashboard" aria-label="Tax summary">
      <div className="tr-dashboard__topline">
        <div>
          <div className="tr-dashboard__heading">{profile.sectionTitle}</div>
          <div className="tr-dashboard__lead">
            {profile.sectionLead} · {yearLabel}
          </div>
        </div>
        <div className="tr-dashboard__yearbox">
          <span>{lang === "de" ? "Steuerjahr" : "Tax year"}</span>
          <strong>{yearLabel}</strong>
        </div>
      </div>

      <div className="tr-summary-strip" role="group" aria-label="Summary totals">
        {showDisposal && (
          <div className="tr-summary-item">
            <div className="tr-summary-item__label">{profile.disposalTitle}</div>
            <div className="tr-summary-item__value">
              {money(totals.disposal, currency, lang)}
            </div>
            <div className="tr-summary-item__meta">{profile.disposalAllowance}</div>
          </div>
        )}
        {showIncome && (
          <div className="tr-summary-item">
            <div className="tr-summary-item__label">{profile.incomeTitle}</div>
            <div className="tr-summary-item__value">
              {money(totals.income, currency, lang)}
            </div>
            <div className="tr-summary-item__meta">{incomeAllowanceMeta}</div>
          </div>
        )}
      </div>

      <div className="tr-breakdown">
        <div className="tr-breakdown__title">
          {lang === "de" ? "Aufschlüsselung nach Bereich" : "Breakdown by category"}
        </div>
        <table className="tr-dashboard-table">
          <colgroup>
            <col className="col-category" />
            {showDisposal && <col className="col-disposal" />}
            {showIncome && <col className="col-income" />}
          </colgroup>
          <thead>
            <tr>
              <th>{categoryLabel}</th>
              {showDisposal && <th>{profile.disposalTitle}</th>}
              {showIncome && <th>{profile.incomeTitle}</th>}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => {
              return (
                <tr key={row.label}>
                  <td>{rowLabel(row.label)}</td>
                  {showDisposal && <td>{money(row.disposal, currency, lang)}</td>}
                  {showIncome && <td>{money(row.income, currency, lang)}</td>}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td>{lang === "de" ? "Gesamt" : "Total"}</td>
              {showDisposal && <td>{money(totals.disposal, currency, lang)}</td>}
              {showIncome && <td>{money(totals.income, currency, lang)}</td>}
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}
